// A line's pick tickets as last approved, kept on the line itself (`routing-line`'s `baseline1`–`3`)
// instead of one `line-pick-ticket` record each: approving a line is then one write, not one per
// pick ticket. `line-pick-ticket` stays for pick tickets placed by hand (Merge, Split, Move), and for
// a line whose baseline does not fit; a hand-placed copy wins over a baseline.
//
// Format: `v1|` then one record per pick ticket, `;` between records and `|` between fields, in
// BASELINE_COLUMNS order, sorted by pick ticket. A field equal to the previous record's is left
// empty (and trailing empties dropped); `\-` is an empty value that differs from the previous one;
// the pick ticket is `+<n>` when it is the previous one plus n. `\` escapes the next character.
// A new version tag (`v2|`) changes it without a migration.

import { asDay } from "./days";
import type { LinePickTicketRecord, RoutingLineRecord } from "./types";

export const BASELINE_FIELDS = ["baseline1", "baseline2", "baseline3"] as const;
export type BaselineField = (typeof BASELINE_FIELDS)[number];
/** One field's length: `NVARCHAR(4000)`, the most SQL takes without MAX. */
export const BASELINE_CHUNK = 4000;

const VERSION = "v1";

/** A copy as the baseline holds it: everything but its id, its line and its load time. */
export type BaselineCopy = Omit<LinePickTicketRecord, "id" | "lineId" | "sourceLoadedAt">;

const BASELINE_COLUMNS = [
  ["pickTicket", "text"],
  ["qty", "number"],
  ["startDate", "day"],
  ["cancelDate", "day"],
  ["ptDate", "day"],
  ["orderNo", "text"],
  ["customerPo", "text"],
  ["store", "text"],
  ["wmsShipment", "text"],
  ["wh", "text"],
  ["corp", "text"],
  ["customerCode", "text"],
  ["customerName", "text"],
  ["div", "text"],
  ["division", "text"],
  ["routingOpenBdays", "number"],
  ["routingDueBdays", "number"],
] as const satisfies readonly (readonly [keyof BaselineCopy, "text" | "number" | "day"])[];

type Kind = (typeof BASELINE_COLUMNS)[number][1];
const EMPTY_TOKEN = "\\-";

const escape = (s: string) => s.replace(/[\\|;+]/g, (c) => `\\${c}`);

/** What a field holds, as text; the CRUD API trims text, so the baseline does too. */
function asText(kind: Kind, v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (kind === "number") return String(Number(v));
  if (kind === "day") return asDay(v) ?? "";
  return String(v).trim();
}

function fromText(kind: Kind, s: string): string | number | null {
  if (kind === "text") return s;
  if (s === "") return null;
  return kind === "number" ? Number(s) : s;
}

const isWhole = (s: string) => /^\d+$/.test(s);

export function encodeBaseline(copies: readonly BaselineCopy[]): string {
  const sorted = [...copies].sort(
    (a, b) => Number(a.pickTicket) - Number(b.pickTicket) || a.pickTicket.localeCompare(b.pickTicket),
  );
  let prev: string[] = BASELINE_COLUMNS.map(() => "");
  const records = sorted.map((c) => {
    const now = BASELINE_COLUMNS.map(([name, kind]) => asText(kind, c[name]));
    const out = now.map((v, i) => {
      if (i === 0 && isWhole(v) && isWhole(prev[0]!) && Number(v) > Number(prev[0])) {
        return `+${Number(v) - Number(prev[0])}`;
      }
      if (i > 0 && v === prev[i]) return "";
      return v === "" ? EMPTY_TOKEN : escape(v);
    });
    prev = now;
    while (out.length > 1 && out.at(-1) === "") out.pop();
    return out.join("|");
  });
  return `${VERSION}|${records.join(";")}`;
}

/** Splits on unescaped `|` and `;`: records of raw (still escaped) fields. */
function split(body: string): string[][] {
  const records: string[][] = [];
  let fields: string[] = [];
  let field = "";
  for (let i = 0; i < body.length; i++) {
    const c = body[i]!;
    if (c === "\\" && i + 1 < body.length) {
      field += c + body[++i];
    } else if (c === "|") {
      fields.push(field);
      field = "";
    } else if (c === ";") {
      fields.push(field);
      records.push(fields);
      fields = [];
      field = "";
    } else field += c;
  }
  fields.push(field);
  records.push(fields);
  return records;
}

const unescape = (raw: string) => raw.replace(/\\(.)/g, "$1");

/** The copies a baseline holds. Throws on a version this code does not read. */
export function decodeBaseline(text: string): BaselineCopy[] {
  if (!text.startsWith(`${VERSION}|`)) throw new Error(`Unknown baseline format "${text.slice(0, 8)}"`);
  const body = text.slice(VERSION.length + 1);
  if (body === "") return [];
  let prev: string[] = BASELINE_COLUMNS.map(() => "");
  return split(body).map((raw) => {
    const now = BASELINE_COLUMNS.map((_, i) => {
      const r = raw[i] ?? "";
      if (r === EMPTY_TOKEN) return "";
      if (i === 0 && r.startsWith("+")) return String(Number(prev[0]) + Number(r.slice(1)));
      return r === "" ? prev[i]! : unescape(r);
    });
    prev = now;
    const copy: Record<string, string | number | null> = {};
    BASELINE_COLUMNS.forEach(([name, kind], i) => {
      copy[name] = fromText(kind, now[i]!);
    });
    return copy as unknown as BaselineCopy;
  });
}

/**
 * The baseline as the line's three fields, or null when it does not fit (the caller then keeps
 * copies). The API trims each field, so no field starts or ends on a space.
 */
export function baselineFields(text: string): Record<BaselineField, string> | null {
  const out = {} as Record<BaselineField, string>;
  let rest = text;
  for (const f of BASELINE_FIELDS) {
    let cut = Math.min(BASELINE_CHUNK, rest.length);
    while (cut > 0 && cut < rest.length && (/\s/.test(rest[cut - 1]!) || /\s/.test(rest[cut]!))) cut--;
    if (cut === 0 && rest.length) return null;
    out[f] = rest.slice(0, cut);
    rest = rest.slice(cut);
  }
  return rest.length ? null : out;
}

/** No baseline: the line keeps its pick tickets as copies (or has none yet). */
export const NO_BASELINE: Record<BaselineField, string> = { baseline1: "", baseline2: "", baseline3: "" };

/** A line's baseline text, or null when it has none. */
export function baselineOf(line: Pick<RoutingLineRecord, BaselineField>): string | null {
  const text = BASELINE_FIELDS.map((f) => line[f] ?? "").join("");
  return text ? text : null;
}

export const BASELINE_ID_PREFIX = "bl:";
/** A copy read from a line's baseline, not a `line-pick-ticket` record: never written by id. */
export const isBaselineCopy = (c: Pick<LinePickTicketRecord, "id">) => c.id.startsWith(BASELINE_ID_PREFIX);

/** A copy without its record fields: what the baseline keeps of it. */
export function baselineEntry(c: Omit<LinePickTicketRecord, "id">): BaselineCopy {
  const { lineId: _line, sourceLoadedAt: _loaded, ...rest } = c;
  return rest;
}
